import React, { useEffect, useState } from 'react';
import { getQueryParams } from 'hookrouter';
import {
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import { APIURL } from '../../services/at-app-api';

const useStyles: any = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
  },
  card: {
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  icon: {
    fontSize: 64,
    marginBottom: theme.spacing(2),
  },
  successIcon: {
    color: theme.palette.success?.main || '#4caf50',
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
}));

type Status = 'loading' | 'success' | 'error';

const UnsubscribePage: React.FC = () => {
  const classes = useStyles();
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = getQueryParams() as { token?: string; channel?: string };
    const { token, channel } = params;

    if (!token || !channel) {
      setMessage('Invalid unsubscribe link. Please contact support.');
      setStatus('error');
      return;
    }

    const validChannel = channel.toLowerCase();
    if (validChannel !== 'email' && validChannel !== 'sms') {
      setMessage('Invalid unsubscribe link. Please contact support.');
      setStatus('error');
      return;
    }

    fetch(`${APIURL}/api/unsubscribe/${validChannel}?token=${encodeURIComponent(token)}`)
      .then(res => {
        if (res.ok) {
          const channelLabel = validChannel === 'email' ? 'email' : 'SMS';
          setMessage(`You have been successfully unsubscribed from ${channelLabel} communications.`);
          setStatus('success');
        } else {
          setMessage('The link is invalid or has expired. Please contact support.');
          setStatus('error');
        }
      })
      .catch(() => {
        setMessage('Something went wrong. Please try again later or contact support.');
        setStatus('error');
      });
  }, []);

  return (
    <div className={classes.root}>
      <Card className={classes.card} elevation={3}>
        <CardContent>
          {status === 'loading' && (
            <>
              <CircularProgress size={48} style={{ marginBottom: 16 }} />
              <Typography variant="h6">Processing your request...</Typography>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircleOutlineIcon className={`${classes.icon} ${classes.successIcon}`} />
              <Typography variant="h5" className={classes.title}>Unsubscribed</Typography>
              <Typography variant="body1" className={classes.subtitle}>{message}</Typography>
            </>
          )}
          {status === 'error' && (
            <>
              <ErrorOutlineIcon className={`${classes.icon} ${classes.errorIcon}`} />
              <Typography variant="h5" className={classes.title}>Unable to Unsubscribe</Typography>
              <Typography variant="body1" className={classes.subtitle}>{message}</Typography>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UnsubscribePage;
